// === Admin: Postcodes (AU) ===
// Full CRUD page — list, add, edit, soft-delete.

import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Pagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { alpha, darken } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { AppTableHead, AppTableHeadCell, AppTableRow, DEFAULT_ROWS_PER_PAGE, ROWS_PER_PAGE_OPTIONS } from '../components/AppTable';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TimezoneOption {
  readonly id: number;
  readonly timezone: string;
  readonly state_id: number | null;
}

interface StateOption {
  readonly state_id: number;
  readonly state: string;
}

interface PostcodeRow {
  readonly id: number;
  readonly suburb: string;
  readonly postcode: string;
  readonly country: string;
  readonly lat: number | null;
  readonly lon: number | null;
  readonly timezone: number | null;
  readonly state: number | null;
}

interface PostcodeRecord {
  readonly id: number;
  readonly suburb: string;
  readonly postcode: string;
  readonly country: string;
  readonly lat: number | null;
  readonly lon: number | null;
  readonly timezone: number | null;
  readonly state: number | null;
  readonly created_at: string | null;
  readonly updated_at: string | null;
  readonly created_by: string | null;
  readonly updated_by: string | null;
}

interface FormState {
  timezone: number | '';
  state: number | '';
  suburb: string;
  postcode: string;
  country: string;
  lat: string;
  lon: string;
}

const DEFAULT_FORM: FormState = {
  timezone: '',
  state: '',
  suburb: '',
  postcode: '',
  country: 'AUSTRALIA',
  lat: '',
  lon: '',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtAuDate(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('en-AU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).format(d);
}

function getApiUrl(): string {
  return (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://localhost:8787';
}

function authHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function PostcodesPage() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const token = session?.access_token ?? '';

  // ---- List state ----
  const [rows, setRows] = useState<readonly PostcodeRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Debounce search input — fire API 350ms after user stops typing
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(0);
      setDebouncedSearch(search);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // ---- Timezones for dropdown ----
  const [timezones, setTimezones] = useState<readonly TimezoneOption[]>([]);
  const [states, setStates] = useState<readonly StateOption[]>([]);

  // ---- Add / Edit dialog ----
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [auditInfo, setAuditInfo] = useState<{
    created_at: string | null; updated_at: string | null;
    created_by: string | null; updated_by: string | null;
  } | null>(null);

  // ---- Delete dialog ----
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteSuburb, setDeleteSuburb] = useState('');
  const [deleting, setDeleting] = useState(false);

  // ---- Snackbar ----
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const showSnack = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnack({ open: true, message, severity });
  };

  // ---------------------------------------------------------------------------
  // Fetch timezones once
  // ---------------------------------------------------------------------------
  const fetchTimezones = useCallback(async () => {
    if (!token) return;
    const res = await fetch(`${getApiUrl()}/api/v1/admin/postcodes/timezones`, {
      headers: authHeaders(token),
    });
    if (res.ok) {
      const json = (await res.json()) as { data: TimezoneOption[] };
      setTimezones(json.data);
    }
  }, [token]);

  const fetchStates = useCallback(async () => {
    if (!token) return;
    const res = await fetch(`${getApiUrl()}/api/v1/admin/postcodes/states`, {
      headers: authHeaders(token),
    });
    if (res.ok) {
      const json = (await res.json()) as { data: StateOption[] };
      setStates(json.data);
    }
  }, [token]);

  useEffect(() => {
    void fetchTimezones();
    void fetchStates();
  }, [fetchTimezones, fetchStates]);

  // ---------------------------------------------------------------------------
  // Fetch list
  // ---------------------------------------------------------------------------
  const fetchList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setListError(null);
    try {
      const url = new URL(`${getApiUrl()}/api/v1/admin/postcodes`);
      url.searchParams.set('page', String(page + 1));
      url.searchParams.set('limit', String(rowsPerPage));
      if (debouncedSearch) url.searchParams.set('search', debouncedSearch);

      const res = await fetch(url.toString(), { headers: authHeaders(token) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { data: PostcodeRow[]; total: number };
      setRows(json.data);
      setTotal(json.total);
    } catch (e) {
      setListError(e instanceof Error ? e.message : 'Failed to load postcodes');
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage, debouncedSearch]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  // ---------------------------------------------------------------------------
  // Auto-fill state when timezone changes
  // ---------------------------------------------------------------------------
  const handleTimezoneChange = (tzId: number) => {
    const tz = timezones.find((t) => t.id === tzId);
    setForm((f) => ({ ...f, timezone: tzId, state: tz?.state_id ?? '' }));
  };

  // ---------------------------------------------------------------------------
  // Open Add dialog
  // ---------------------------------------------------------------------------
  const openAdd = () => {
    setEditingId(null);
    setForm(DEFAULT_FORM);
    setFormError(null);
    setAuditInfo(null);
    setDialogOpen(true);
  };

  // ---------------------------------------------------------------------------
  // Open Edit dialog
  // ---------------------------------------------------------------------------
  const openEdit = async (id: number) => {
    if (!token) return;
    setFormError(null);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/postcodes/${id}`, {
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error('Failed to load record');
      const json = (await res.json()) as { data: PostcodeRecord };
      const r = json.data;
      setForm({
        timezone: r.timezone ?? '',
        state: r.state ?? '',
        suburb: r.suburb,
        postcode: r.postcode,
        country: r.country,
        lat: r.lat !== null ? String(r.lat) : '',
        lon: r.lon !== null ? String(r.lon) : '',
      });
      setAuditInfo({
        created_at: r.created_at ?? null,
        updated_at: r.updated_at ?? null,
        created_by: r.created_by ?? null,
        updated_by: r.updated_by ?? null,
      });
      setEditingId(id);
      setDialogOpen(true);
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Failed to load record', 'error');
    }
  };

  // ---------------------------------------------------------------------------
  // Save (create or update)
  // ---------------------------------------------------------------------------
  const handleSave = async () => {
    if (!form.suburb.trim() || !form.postcode.trim() || form.lat === '' || form.lon === '' || form.timezone === '') {
      setFormError('Suburb, Postcode, Timezone, Latitude and Longitude are required.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const payload = {
        timezone: Number(form.timezone),
        state:    form.state !== '' ? Number(form.state) : null,
        suburb:   form.suburb.trim(),
        postcode: form.postcode.trim(),
        country:  form.country || 'AUSTRALIA',
        lat:      parseFloat(form.lat),
        lon:      parseFloat(form.lon),
      };
      const url = editingId
        ? `${getApiUrl()}/api/v1/admin/postcodes/${editingId}`
        : `${getApiUrl()}/api/v1/admin/postcodes`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: authHeaders(token),
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      setDialogOpen(false);
      showSnack(editingId ? 'Postcode updated.' : 'Postcode added.');
      void fetchList();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Delete
  // ---------------------------------------------------------------------------
  const confirmDelete = (id: number, suburb: string) => {
    setDeleteId(id);
    setDeleteSuburb(suburb);
  };

  const handleDelete = async () => {
    if (!deleteId || !token) return;
    setDeleting(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/postcodes/${deleteId}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDeleteId(null);
      showSnack('Postcode deleted.');
      void fetchList();
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Delete failed', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <>
      <Container maxWidth="xl" sx={{ py: 4 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <Tooltip title="Back to Dashboard">
            <IconButton onClick={() => navigate('/dashboard')} size="small">
              <BackIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>Postcodes (AU)</Typography>
            <Typography variant="body2" color="text.secondary">
              Australian postcode reference data
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Add Postcode
          </Button>
        </Box>

        {/* Toolbar: search */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search suburb, postcode…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 300 }}
            />
            {search && (
              <Button
                size="small"
                onClick={() => { setSearch(''); setPage(0); }}
              >
                Clear
              </Button>
            )}
            <Box sx={{ ml: 'auto' }}>
              <Chip label={`${total} records`} size="small" variant="outlined" />
            </Box>
          </Box>
        </Paper>

        {/* Error */}
        {listError && (
          <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>
        )}

        {/* Table */}
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer>
            <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
              <AppTableHead>
                <TableRow>
                  <AppTableHeadCell>Suburb</AppTableHeadCell>
                  <AppTableHeadCell>State</AppTableHeadCell>
                  <AppTableHeadCell>Postcode</AppTableHeadCell>
                  <AppTableHeadCell>Country</AppTableHeadCell>
                  <AppTableHeadCell>Timezone</AppTableHeadCell>
                  <AppTableHeadCell>Lat</AppTableHeadCell>
                  <AppTableHeadCell>Lon</AppTableHeadCell>
                  <AppTableHeadCell align="right" sx={{ width: 96 }}>Actions</AppTableHeadCell>
                </TableRow>
              </AppTableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      No records found
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <AppTableRow key={row.id}>
                      <TableCell>{row.suburb}</TableCell>
                      <TableCell>{states.find((s) => s.state_id === row.state)?.state ?? '—'}</TableCell>
                      <TableCell>{row.postcode}</TableCell>
                      <TableCell>{row.country}</TableCell>
                      <TableCell>{timezones.find((t) => t.id === row.timezone)?.timezone ?? row.timezone ?? '—'}</TableCell>
                      <TableCell>{row.lat ?? '—'}</TableCell>
                      <TableCell>{row.lon ?? '—'}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => void openEdit(row.id)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => confirmDelete(row.id, row.suburb)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </AppTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {/* Pagination bar */}
          <Box sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: theme.palette.mode === 'light'
              ? alpha(theme.palette.primary.main, 0.09)
              : darken(theme.palette.background.paper, 0.35),
            px: 2,
            py: 1.5,
          })}>
            {/* Left: showing range */}
            <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85 })}>
              {loading ? 'Loading…' : total === 0 ? 'No records found' :
                `Showing ${page * rowsPerPage + 1} to ${Math.min((page + 1) * rowsPerPage, total)} of ${total} entries`
              }
            </Typography>

            {/* Right: rows-per-page + page info + page buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Rows per page */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}>Show</Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
                    '& .MuiSvgIcon-root': { color: theme.palette.text.primary },
                    '& .MuiSelect-select': { py: 0.5, px: 1 },
                  })}
                >
                  {ROWS_PER_PAGE_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n}>{n}</MenuItem>
                  ))}
                </Select>
              </Box>

              {/* Page X of Y */}
              {total > 0 && (
                <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}>
                  Page {page + 1} of {Math.ceil(total / rowsPerPage)}
                </Typography>
              )}

              {/* Numbered pagination */}
              {Math.ceil(total / rowsPerPage) > 1 && (
                <Pagination
                  count={Math.ceil(total / rowsPerPage)}
                  page={page + 1}
                  onChange={(_, p) => setPage(p - 1)}
                  color="primary"
                  size="small"
                  disabled={loading}
                  sx={(theme) => ({
                    '& .MuiPaginationItem-root': {
                      color: theme.palette.text.primary,
                      borderColor: theme.palette.divider,
                      '&:hover': { backgroundColor: theme.palette.action.hover },
                    },
                    '& .Mui-selected': {
                      backgroundColor: `${theme.palette.primary.main} !important`,
                      color: theme.palette.primary.contrastText,
                    },
                    '& .Mui-disabled': { opacity: 0.5 },
                  })}
                />
              )}
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* ------------------------------------------------------------------ */}
      {/* Add / Edit Dialog                                                    */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Postcode' : 'Add Postcode'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          {formError && <Alert severity="error">{formError}</Alert>}

          {/* Timezone */}
          <FormControl size="small" fullWidth required>
            <InputLabel>Timezone</InputLabel>
            <Select
              label="Timezone"
              value={form.timezone}
              onChange={(e) => {
                const val = Number(e.target.value);
                handleTimezoneChange(val);
              }}
            >
              {timezones.map((tz) => (
                <MenuItem key={tz.id} value={tz.id}>{tz.timezone}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Suburb + Postcode */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Suburb"
              size="small"
              fullWidth
              required
              inputProps={{ maxLength: 100 }}
              value={form.suburb}
              onChange={(e) => setForm((f) => ({ ...f, suburb: e.target.value }))}
            />
            <TextField
              label="Postcode"
              size="small"
              sx={{ width: 140 }}
              required
              inputProps={{ maxLength: 20 }}
              value={form.postcode}
              onChange={(e) => setForm((f) => ({ ...f, postcode: e.target.value }))}
            />
          </Box>

          {/* State (readonly - derived from timezone) */}
          <TextField
            label="State"
            size="small"
            fullWidth
            value={form.state !== '' ? (states.find((s) => s.state_id === form.state)?.state ?? form.state) : ''}
            InputProps={{ readOnly: true }}
            helperText="Auto-filled from timezone selection"
          />

          {/* Country (readonly) */}
          <TextField
            label="Country"
            size="small"
            fullWidth
            value={form.country}
            InputProps={{ readOnly: true }}
          />

          {/* Lat + Lon */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Latitude"
              size="small"
              fullWidth
              required
              type="number"
              inputProps={{ step: 'any' }}
              value={form.lat}
              onChange={(e) => setForm((f) => ({ ...f, lat: e.target.value }))}
            />
            <TextField
              label="Longitude"
              size="small"
              fullWidth
              required
              type="number"
              inputProps={{ step: 'any' }}
              value={form.lon}
              onChange={(e) => setForm((f) => ({ ...f, lon: e.target.value }))}
            />
          </Box>

          {/* Audit Trail */}
          {editingId && auditInfo && (
            <Box
              mt={1}
              p={2}
              sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.12), borderRadius: 1 }}
            >
              <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1} sx={{ textTransform: 'uppercase', letterSpacing: 0.8, textAlign: 'center' }}>
                Audit Trail
              </Typography>
              <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.disabled" display="block">Created</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fmtAuDate(auditInfo.created_at)}{auditInfo.created_by ? <> &mdash; <strong>{auditInfo.created_by}</strong></> : ''}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.disabled" display="block">Updated</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fmtAuDate(auditInfo.updated_at)}{auditInfo.updated_by ? <> &mdash; <strong>{auditInfo.updated_by}</strong></> : ''}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} /> : undefined}
          >
            {editingId ? 'Save Changes' : 'Add Postcode'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Delete Confirmation                                                  */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={deleteId !== null} onClose={() => setDeleteId(null)} maxWidth="xs">
        <DialogTitle>Delete Postcode</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>{deleteSuburb}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleDelete()}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} /> : undefined}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Snackbar                                                             */}
      {/* ------------------------------------------------------------------ */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}
